import { cn } from "@/lib/utils";
import { ChevronRight, Home } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Quiet LMS page header: optional breadcrumb + icon + title + muted subtitle.
 *
 * @param {string} [breadcrumb] - Current section label (renders Home > label)
 * @param {Array<{label: string, href?: string}>} [breadcrumbs] - Full trail; overrides `breadcrumb`
 */
export function PageHeader({
  title,
  subtitle,
  icon,
  actions,
  breadcrumb,
  breadcrumbs,
  className,
  children,
}) {
  const trail =
    breadcrumbs?.length > 0
      ? breadcrumbs
      : breadcrumb
        ? [
            { label: "Home", href: "/home/dashboard" },
            { label: breadcrumb },
          ]
        : null;

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {trail ? (
        <nav aria-label="Breadcrumb">
          <ol className="m-0 flex list-none items-center gap-1 p-0 md:gap-2">
            {trail.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === trail.length - 1;

              return (
                <li
                  key={`${item.label}-${index}`}
                  className="m-0 flex list-none items-center gap-1 p-0 md:gap-2"
                >
                  {!isFirst ? (
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-zinc-400"
                      aria-hidden="true"
                    />
                  ) : null}
                  {item.href && !isLast ? (
                    <Link
                      to={item.href}
                      className="inline-flex items-center text-sm font-medium text-zinc-600 hover:text-primary"
                    >
                      {isFirst ? (
                        <Home className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      ) : null}
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      className={cn(
                        "inline-flex items-center text-sm font-medium",
                        isLast ? "text-primary" : "text-zinc-600"
                      )}
                    >
                      {isFirst ? (
                        <Home className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      ) : null}
                      {item.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2.5">
            {icon ? (
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-muted text-primary shrink-0">
                {icon}
              </div>
            ) : null}
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
              {title}
            </h1>
          </div>
          {subtitle ? (
            <p className="text-sm text-zinc-500 md:text-[15px]">{subtitle}</p>
          ) : null}
          {children}
        </div>
        {actions ? (
          <div className="flex items-center gap-2 shrink-0">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}

export default PageHeader;
